import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

const TABLE_GLB_URL = `/models/${encodeURIComponent("Tilbe Web.glb")}`;
const TABLE_PARAMETRIC_MESH_COUNT = 13;
const CHAIR_GLB_URL = `/models/${encodeURIComponent("Colba_Stool_v2.glb")}`;

/**
 * Cached chair GLB root (same JS realm as the iframe). Survives React Strict Mode remount
 * and avoids a frame of procedural “fake” chairs while `useRef` is still null after reload.
 */
let chairGlbSceneSingleton: THREE.Object3D | null = null;
let chairGlbLoadFailed = false;

/** Diffuse map embedded in Tilbe Web.glb (set when GLTFLoader finishes in the browser). */
let tableGlbEmbeddedWoodMap: THREE.Texture | null = null;
/** True when we had to synthesize UVs (model export had no TEXCOORD_0). */
let tableGlbUsesProceduralUV = false;

/**
 * Orimba Configurator V23 — Build-safe, Debugged, and UX-polished
 * - Sticky, maximized 3D viewer (left). Settings scroll independently (right).
 * - "Reframe view" button next to the chairs badge in the 3D overlay.
 * - Advanced Table Settings: Top thickness, Leg width, Apron height, Leg inset, Show leg gap.
 * - Advanced Chair Settings: Chair width, Chair depth, Back height, Clearance.
 * - Timber choices: Eucalyptus, Feature Eucalyptus, American Oak, Japanese Black [American Oak], Blackwood (affects color & default $/m³).
 * - Sled legs: Top rail REMOVED (bottom rail only). Rails share the same X (width) & Z (thickness) as posts.
 * - Short-side aprons are FLUSH to the sled post inner face (so they never protrude beyond the legs).
 * - Dynamic calibrated pricing from client-provided anchors (per mm), scaled breakdown retained.
 * - Dynamic OrbitControls import; WebGL checks & graceful fallback overlays.
 */

// ---------- Utilities ----------
export const mm3_to_m3 = (v: number) => v / 1e9;
export const mm2_to_m2 = (v: number) => v / 1e6;
export const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

function scaleGltfRootToMmIfNeeded(root: THREE.Object3D) {
  if (root.userData.gltfScaledToMm) return;
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(root);
  if (box.isEmpty()) return;
  const s = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(s.x, s.y, s.z);
  if (maxDim > 0 && maxDim < 100) {
    root.scale.multiplyScalar(1000);
    root.updateMatrixWorld(true);
    root.userData.gltfScaledToMm = true;
  }
}

/**
 * Tilbe Web.glb: mesh_0 … mesh_12 (direct index).
 * Legacy 250815_Tilbe Table_v2.glb: Mesh_0001 = Blender Mesh_0.001 → floor(n/1000).
 */
function parseTableMeshIndex(name: string): number {
  const direct = name.match(/^mesh_(\d+)$/i);
  if (direct) return parseInt(direct[1], 10);
  const legacy = name.match(/^Mesh_(\d+)$/);
  if (legacy) return Math.floor(parseInt(legacy[1], 10) / 1000);
  return -1;
}


type TableMeshRole = "top" | "endApron" | "sideApron" | "leg" | "corner";

/** Per-mesh roles for Tilbe Web.glb (Top / Legs / Apron groups, 13 meshes). */
const TABLE_MESH_ROLE_BY_INDEX: Record<number, TableMeshRole> = {
  0: "top",
  1: "leg",
  2: "leg",
  3: "leg",
  4: "leg",
  5: "leg",
  6: "leg",
  7: "leg",
  8: "leg",
  9: "sideApron",
  10: "endApron",
  11: "endApron",
  12: "sideApron",
};

type TableMeshBase = { center: THREE.Vector3; halfSize: THREE.Vector3 };
type TableGlbBaseMetrics = {
  fullW: number;
  fullD: number;
  fullH: number;
  meshes: Record<number, TableMeshBase>;
};

/** Tilbe Web.glb exports without TEXCOORD_0; planar UV from bounds so Oak Light maps render. */
const WOOD_UV_TILES_PER_M = 0.5;

function captureTableGlbEmbeddedWood(root: THREE.Object3D) {
  tableGlbEmbeddedWoodMap = null;
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      if (mat instanceof THREE.MeshStandardMaterial && mat.map && !tableGlbEmbeddedWoodMap) {
        tableGlbEmbeddedWoodMap = mat.map;
      }
    }
  });
}

function assignPlanarUVFromBounds(geometry: THREE.BufferGeometry) {
  if (geometry.attributes.uv) return;
  tableGlbUsesProceduralUV = true;
  const pos = geometry.getAttribute("position") as THREE.BufferAttribute | undefined;
  if (!pos) return;

  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box || box.isEmpty()) return;

  const size = new THREE.Vector3();
  box.getSize(size);
  const sx = Math.max(size.x, 1e-6);
  const sy = Math.max(size.y, 1e-6);
  const sz = Math.max(size.z, 1e-6);

  const axes = [
    { thin: sx, uAxis: 1, vAxis: 2, minU: box.min.y, minV: box.min.z, su: sy, sv: sz },
    { thin: sy, uAxis: 0, vAxis: 2, minU: box.min.x, minV: box.min.z, su: sx, sv: sz },
    { thin: sz, uAxis: 0, vAxis: 1, minU: box.min.x, minV: box.min.y, su: sx, sv: sy },
  ];
  axes.sort((a, b) => a.thin - b.thin);
  const { uAxis, vAxis, minU, minV, su, sv } = axes[0];
  const tileU = Math.max(su * WOOD_UV_TILES_PER_M, 0.2);
  const tileV = Math.max(sv * WOOD_UV_TILES_PER_M, 0.2);
  // Tabletop / horizontal slabs (thin Y): Wood 37 grain runs along texture V; swap UV so grain follows width (X).
  const swapGrainToWidth =
    axes[0].thin === sy && axes[0].uAxis === 0 && axes[0].vAxis === 2;

  const coord = (i: number, axis: number) =>
    axis === 0 ? pos.getX(i) : axis === 1 ? pos.getY(i) : pos.getZ(i);

  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    const u = ((coord(i, uAxis) - minU) / su) * tileU;
    const v = ((coord(i, vAxis) - minV) / sv) * tileV;
    if (swapGrainToWidth) {
      uv[i * 2] = v;
      uv[i * 2 + 1] = u;
    } else {
      uv[i * 2] = u;
      uv[i * 2 + 1] = v;
    }
  }
  geometry.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
}

/** Center each mesh geometry at origin so pivots can use world-mm targets reliably. */
function centerTableGlbMeshGeometries(root: THREE.Object3D) {
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry || mesh.userData.tableGlbCentered) return;
    const geo = mesh.geometry;
    geo.computeBoundingBox();
    const box = geo.boundingBox;
    if (!box || box.isEmpty()) return;
    const c = box.getCenter(new THREE.Vector3());
    geo.translate(-c.x, -c.y, -c.z);
    assignPlanarUVFromBounds(geo);
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.scale.set(1, 1, 1);
    mesh.userData.tableGlbCentered = true;
  });
}

function captureTableGlbBaseMetrics(root: THREE.Object3D): TableGlbBaseMetrics {
  root.updateMatrixWorld(true);
  const full = new THREE.Box3().setFromObject(root);
  const fullSize = full.getSize(new THREE.Vector3());
  const meshes: Record<number, TableMeshBase> = {};
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const idx = parseTableMeshIndex(mesh.name);
    if (idx < 0) return;
    mesh.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(mesh);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    meshes[idx] = {
      center: center.clone(),
      halfSize: size.multiplyScalar(0.5),
    };
  });
  return {
    fullW: Math.max(1e-6, fullSize.x),
    fullD: Math.max(1e-6, fullSize.z),
    fullH: Math.max(1e-6, fullSize.y),
    meshes,
  };
}

/**
 * Parametric resize under the GLB root (keeps ×1000 mm scale on root).
 * Pivots stay in pre-scale local space; geometry is centered at each pivot origin.
 */
function applyParametricTableGlb(
  tableRoot: THREE.Object3D,
  w: number,
  d: number,
  h: number,
  base: TableGlbBaseMetrics
): number {
  const unitScale = Math.max(tableRoot.scale.x, tableRoot.scale.y, tableRoot.scale.z, 1);
  const toLocal = (mm: number) => mm / unitScale;
  const sx = w / base.fullW;
  const sz = d / base.fullD;
  const sy = h / base.fullH;

  const meshes: THREE.Mesh[] = [];
  tableRoot.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh) meshes.push(mesh);
  });

  let pivotCount = 0;
  for (const mesh of meshes) {
    const idx = parseTableMeshIndex(mesh.name);
    const meta = base.meshes[idx];
    const role = TABLE_MESH_ROLE_BY_INDEX[idx];
    if (!meta || !role) {
      console.warn("[Orimba] Unmapped table mesh:", mesh.name, "idx", idx);
      continue;
    }

    const parent = mesh.parent;
    if (!parent) continue;
    parent.remove(mesh);

    let scaleX = 1;
    let scaleY = 1;
    let scaleZ = 1;
    let posX = toLocal(meta.center.x * sx);
    let posY = toLocal(meta.center.y * sy);
    let posZ = toLocal(meta.center.z * sz);

    switch (role) {
      case "top":
        scaleX = sx;
        scaleZ = sz;
        posY = toLocal(meta.center.y * sy);
        break;
      case "endApron":
        scaleZ = sz;
        break;
      case "sideApron":
        scaleX = sx;
        break;
      case "leg":
      case "corner":
        // Geometry was centered on its original world center; anchor pivots there (not halfSize).
        scaleY = sy;
        posY = toLocal(meta.center.y * sy);
        break;
      default:
        break;
    }

    const pivot = new THREE.Group();
    pivot.position.set(posX, posY, posZ);
    pivot.scale.set(scaleX, scaleY, scaleZ);
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.scale.set(1, 1, 1);
    pivot.add(mesh);
    parent.add(pivot);
    pivotCount++;
  }

  if (pivotCount < TABLE_PARAMETRIC_MESH_COUNT) {
    console.warn(
      "[Orimba] Parametric table pivots incomplete:",
      pivotCount,
      "/",
      TABLE_PARAMETRIC_MESH_COUNT
    );
  }
  return pivotCount;
}

/** Each placed chair gets its own material instances (textures still shared); template stays pristine. */
function cloneMeshMaterialsPerInstance(root: THREE.Object3D) {
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    if (Array.isArray(mesh.material)) {
      mesh.material = mesh.material.map((m) => m.clone());
    } else {
      mesh.material = mesh.material.clone();
    }
  });
}

/** Uniform scale so clone fits inside target mm box; origin at floor center. */
function prepareChairGlbClone(
  template: THREE.Object3D,
  targetW: number,
  targetD: number,
  targetH: number
) {
  const chair = template.clone(true);
  cloneMeshMaterialsPerInstance(chair);
  chair.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(chair);
  const size = box.getSize(new THREE.Vector3());
  if (size.x > 0 && size.y > 0 && size.z > 0) {
    const s = Math.min(targetW / size.x, targetD / size.z, targetH / size.y);
    chair.scale.multiplyScalar(s);
    chair.updateMatrixWorld(true);
    const box2 = new THREE.Box3().setFromObject(chair);
    const cx = (box2.min.x + box2.max.x) / 2;
    const cz = (box2.min.z + box2.max.z) / 2;
    chair.position.set(-cx, -box2.min.y, -cz);
  }
  chair.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    }
  });
  return chair;
}

/** Remove GLTF maps so table Tint / Oak fully drives appearance. */
function stripStandardMaterialTextureMaps(m: THREE.MeshStandardMaterial) {
  m.map = null;
  m.lightMap = null;
  m.normalMap = null;
  m.roughnessMap = null;
  m.metalnessMap = null;
  m.emissiveMap = null;
  m.bumpMap = null;
  m.displacementMap = null;
  m.alphaMap = null;
  m.aoMap = null;
  m.envMap = null;
  if (m instanceof THREE.MeshPhysicalMaterial) {
    m.clearcoatMap = null;
    m.clearcoatNormalMap = null;
    m.sheenColorMap = null;
    m.specularIntensityMap = null;
    m.specularColorMap = null;
    m.iridescenceMap = null;
    m.iridescenceThicknessMap = null;
    m.transmissionMap = null;
    m.thicknessMap = null;
    m.anisotropyMap = null;
  }
}

// ---------- Pricing helpers ----------
const PRICING_DEFAULTS = {
  WOOD_PRICE_PER_M3: 5200, // AUD per m^3
  FINISH_PRICE_PER_M2: 75, // AUD per m^2
  LABOUR_RATE_PER_HR: 95, // AUD/hour
  DELIVERY_FLAT: 180, // AUD (metro)
  EST_ASSEMBLY_HOURS_BASE: 3.5, // baseline hours per table
  EST_ASSEMBLY_HOURS_PER_M: 1.2, // extra hours per meter of (W + D) in meters
  MARKUP_PERCENT: 35, // % before GST
  GST_PERCENT: 10, // AU GST
};

// Timber 纯色（仅在 TintOnly 模式下适用）
const TIMBER_SPECS: Record<string, { color: number; price: number }> = {
  Eucalyptus: { color: 0xead3b0, price: 5200 },
  "Feature Eucalyptus": { color: 0xd7ba89, price: 5400 },
  "American Oak": { color: 0xc8a86a, price: 5800 },
  /** Stained finish; tint-only preview slightly darker than natural American Oak */
  "Japanese Black [American Oak]": { color: 0x3d2e22, price: 6000 },
  Blackwood: { color: 0x8b6b4a, price: 7200 },
};

// Calibrated price anchors (inc. GST) per timber at default preset sizes (PDP RRP inc GST).
// Feature Eucalyptus and American Oak share the same column; Japanese Black [American Oak] is priced separately.
// Each item: [width_mm, depth_mm, { Eucalyptus, Feature, American, JapaneseBlackOak, Blackwood }]
const CALIBRATED_ANCHORS: Array<[number, number, Record<string, number>]> = [
  [1600, 900, { Eucalyptus: 2795, Feature: 3255, American: 3255, JapaneseBlackOak: 3505, Blackwood: 3675 }],
  [1800, 900, { Eucalyptus: 2955, Feature: 3445, American: 3445, JapaneseBlackOak: 3695, Blackwood: 3890 }],
  [2100, 1000, { Eucalyptus: 3380, Feature: 3955, American: 3955, JapaneseBlackOak: 4245, Blackwood: 4465 }],
  [2400, 1050, { Eucalyptus: 3750, Feature: 4400, American: 4400, JapaneseBlackOak: 4650, Blackwood: 4965 }],
  [2700, 1100, { Eucalyptus: 4315, Feature: 5080, American: 5080, JapaneseBlackOak: 5330, Blackwood: 5725 }],
  [3000, 1100, { Eucalyptus: 5010, Feature: 5910, American: 5910, JapaneseBlackOak: 6160, Blackwood: 6665 }],
];

function timberGroupKey(key: string) {
  if (key === "Feature Eucalyptus") return "Feature";
  if (key === "Japanese Black [American Oak]") return "JapaneseBlackOak";
  if (key === "American Oak") return "American";
  if (key === "Blackwood") return "Blackwood";
  return "Eucalyptus";
}

/** Map Shopify variant option label to configurator timber keys */
export function normalizeTimberLabel(s: string): keyof typeof TIMBER_SPECS {
  const t = (s || "").trim().toLowerCase();
  if (t.includes("blackwood")) return "Blackwood";
  if (t.includes("japanese") && t.includes("black") && !t.includes("blackwood")) {
    return "Japanese Black [American Oak]";
  }
  if (t.includes("american") && t.includes("oak")) return "American Oak";
  if (t.includes("feature")) return "Feature Eucalyptus";
  if (t.includes("eucalyptus")) return "Eucalyptus";
  return "Eucalyptus";
}

function calibratedPriceIncGST(w: number, d: number, h: number, timberKey: string) {
  const tKey = timberGroupKey(timberKey);
  const anchors = CALIBRATED_ANCHORS;
  const wMin = anchors[0][0];
  const wMax = anchors[anchors.length - 1][0];
  const W = Math.max(wMin, Math.min(w, wMax));

  let i = 0;
  for (let k = 0; k < anchors.length - 1; k++) {
    if (W >= anchors[k][0] && W <= anchors[k + 1][0]) {
      i = k;
      break;
    }
  }
  const [w1, d1, p1s] = anchors[i];
  const [w2, d2, p2s] = anchors[Math.min(i + 1, anchors.length - 1)];
  const p1 = p1s[tKey];
  const p2 = p2s[tKey];

  const t = (W - w1) / Math.max(1, w2 - w1);
  const basePrice = p1 + (p2 - p1) * t;
  const baseDepth = d1 + (d2 - d1) * t;

  // 简单的深度校正：按面积斜率近似
  const a1 = w1 * d1;
  const a2 = w2 * d2;
  const areaSlope = (p2 - p1) / Math.max(1, a2 - a1); // $ per mm^2
  const depthAdj = areaSlope * (W * (d - baseDepth));

  const heightAdjPerMm = 0;
  const heightAdj = heightAdjPerMm * (h - 750);

  return basePrice + depthAdj + heightAdj;
}

// Fallback if GLB texture fails to load in the browser.
const texLoader = new THREE.TextureLoader();
const wood37FallbackMap = texLoader.load(
  `/models/${encodeURIComponent("Wood37_extracted.png")}`
);
wood37FallbackMap.colorSpace = THREE.SRGBColorSpace;
wood37FallbackMap.wrapS = wood37FallbackMap.wrapT = THREE.RepeatWrapping;
wood37FallbackMap.minFilter = THREE.LinearMipmapLinearFilter;
wood37FallbackMap.magFilter = THREE.LinearFilter;

function configureWood37Map(map: THREE.Texture) {
  map.colorSpace = THREE.SRGBColorSpace;
  map.wrapS = THREE.RepeatWrapping;
  map.wrapT = THREE.RepeatWrapping;
  map.needsUpdate = true;
}

/** Prefer GLB-loader map + original PBR; only fall back to extracted PNG. */
function applyWood37Surface(m: THREE.MeshStandardMaterial) {
  const map = m.map ?? tableGlbEmbeddedWoodMap;
  if (map) {
    if (!m.map) m.map = map;
    configureWood37Map(m.map);
    m.vertexColors = false;
    m.metalness = 0;
    m.transparent = false;
    m.opacity = 1;
    m.depthWrite = true;
    m.color.set(0xffffff);
    m.emissive?.set?.(0x000000);
    if (typeof m.emissiveIntensity === "number") m.emissiveIntensity = 0;
    m.needsUpdate = true;
    return;
  }
  stripStandardMaterialTextureMaps(m);
  m.map = wood37FallbackMap;
  configureWood37Map(m.map);
  m.roughnessMap = null;
  m.bumpMap = null;
  m.bumpScale = 0;
  m.roughness = 0.72;
  m.metalness = 0;
  m.color.set(0xffffff);
  m.emissive.set(0x000000);
  m.emissiveIntensity = 0;
  m.needsUpdate = true;
}

// ---------- Chair math ----------
export function estimateLinearCount(runLen: number, chairWidth: number, clearance: number, margin: number) {
  const usable = runLen - 2 * margin;
  if (usable < chairWidth) return 0;
  const spacing = chairWidth + clearance;
  return Math.floor((usable - chairWidth) / spacing) + 1;
}
export function centeredPositions(runLen: number, chairWidth: number, clearance: number, margin: number) {
  const count = estimateLinearCount(runLen, chairWidth, clearance, margin);
  if (count <= 0) return [] as number[];
  const spacing = chairWidth + clearance;
  const usable = runLen - 2 * margin;
  const span = chairWidth + (count - 1) * spacing;
  const leftover = usable - span;
  const start = margin + leftover / 2 + chairWidth / 2;
  return Array.from({ length: count }, (_, i) => start + i * spacing);
}

type MaterialMode = "TintOnly" | "OakLight" | "LineTransparent";
type RenderStyle = "Solid" | "LineArt";

export default function OrimbaConfigurator() {
  // Limits
  const MIN_W = 1600, MAX_W = 3000;
  const MIN_D = 900, MAX_D = 1200;
  const MIN_H = 750, MAX_H = 760;

  // Leg/rail clearances
  const LEG_CLEAR_EDGE = 20;
  const LEG_DEPTH_MARGIN = 60;
  const LEG_THICKNESS_FIXED = 34;

  // Apron constants
  const APRON_ON = true;
  const APRON_THICK = 40;

  // Chair defaults
  const DEFAULT_CHAIR_WIDTH = 480;
  const DEFAULT_CHAIR_DEPTH = 500;
  const DEFAULT_CHAIR_BACK = 850;
  const DEFAULT_CLEARANCE = 120;
  const CHAIR_CORNER_MARGIN = 80;
  const CHAIR_OUT_OFFSET = 180;
  const MAX_TUCK = 600;

  // Table & geometry state
  const [width, setWidth] = useState(2000);
  const [depth, setDepth] = useState(1000);
  const [height, setHeight] = useState(760);
  const [legInset, setLegInset] = useState(250);

  // Sliders
  const [topThick, setTopThick] = useState(32);
  const [legBar, setLegBar] = useState(60);
  const [apronH, setApronH] = useState(80);

  // Chairs state
  const [chairWidth, setChairWidth] = useState(DEFAULT_CHAIR_WIDTH);
  const [chairDepth, setChairDepth] = useState(DEFAULT_CHAIR_DEPTH);
  const [chairBackHeight, setChairBackHeight] = useState(DEFAULT_CHAIR_BACK);
  const [chairClearance, setChairClearance] = useState(DEFAULT_CLEARANCE);
  const [includeEndChairs, setIncludeEndChairs] = useState(true);
  const [includeSideChairs, setIncludeSideChairs] = useState(true);
  /** When false, chair meshes stay built but chairsGroup.visible is off (count & layout unchanged). */
  const [showChairModels, setShowChairModels] = useState(false);
  const [pushChairsIn, setPushChairsIn] = useState(false);
  const [tuckDepth, setTuckDepth] = useState(150);
  const [chairCount, setChairCount] = useState(0);

  // Dims toggle
  const [showLegGapDim, setShowLegGapDim] = useState(false);

  // Pricing knobs
  const [woodPrice, setWoodPrice] = useState(PRICING_DEFAULTS.WOOD_PRICE_PER_M3);
  const [finishPrice, setFinishPrice] = useState(PRICING_DEFAULTS.FINISH_PRICE_PER_M2);
  const [labourRate, setLabourRate] = useState(PRICING_DEFAULTS.LABOUR_RATE_PER_HR);
  const [deliveryFlat, setDeliveryFlat] = useState(PRICING_DEFAULTS.DELIVERY_FLAT);
  const [markupPercent, setMarkupPercent] = useState(PRICING_DEFAULTS.MARKUP_PERCENT);
  const [gstPercent, setGstPercent] = useState(PRICING_DEFAULTS.GST_PERCENT);
  const [includeDelivery, setIncludeDelivery] = useState(true);

  // Default material mode: Line Transparent
  const [matMode, setMatMode] = useState<MaterialMode>("LineTransparent");
  const [timberKey, setTimberKey] =
    useState<keyof typeof TIMBER_SPECS>("Eucalyptus");
  const [renderStyle, setRenderStyle] = useState<RenderStyle>("Solid");
  const [isRangeDragging, setIsRangeDragging] = useState(false);

  // Init status
  const [initError, setInitError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  /** Shopify product page iframe: hide local sliders; drive via postMessage */
  const [embedShopify, setEmbedShopify] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      const q = new URLSearchParams(window.location.search).get("shopify");
      if (q === "1" || q === "true") return true;
      return window.self !== window.top;
    } catch {
      return false;
    }
  });

  // NOTE: Surface (Tint/Oak/LineTransparent) and Render style (Solid/LineArt) are intentionally independent.

  // Refs
  const mountRef = useRef<HTMLDivElement | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const tableGroupRef = useRef<THREE.Group | null>(null);
  const dimsGroupRef = useRef<THREE.Group | null>(null);
  const chairsGroupRef = useRef<THREE.Group | null>(null);
  const tableGlbTemplateRef = useRef<THREE.Object3D | null>(null);
  const tableGlbBaseMetricsRef = useRef<TableGlbBaseMetrics | null>(null);
  const chairGlbTemplateRef = useRef<THREE.Object3D | null>(null);
  const hemiLightRef = useRef<THREE.HemisphereLight | null>(null);
  const dirLightRef = useRef<THREE.DirectionalLight | null>(null);
  const ambientLightRef = useRef<THREE.AmbientLight | null>(null);
  const lineArtFillMatRef = useRef(
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      colorWrite: false,
      transparent: false,
      depthWrite: true,
      depthTest: true,
    })
  );
  const lineArtTransparentMatRef = useRef(
    new THREE.MeshStandardMaterial({
      color: 0xe6e6e6,
      metalness: 0,
      roughness: 0.95,
      transparent: false,
      opacity: 1,
      depthWrite: true,
      depthTest: true,
    })
  );
  const [glbReadyTick, setGlbReadyTick] = useState(0);
  const [chairGlbReadyTick, setChairGlbReadyTick] = useState(0);
  /** Bumps on each Shopify postMessage so we can auto-fit camera once on first embed sync. */
  const [shopifySyncTick, setShopifySyncTick] = useState(0);
  /** Bumps after parent sends a non-streaming mm commit (variant / pointerup flush) so full rebuild can run. */
  const [shopifyParentDragIdleTick, setShopifyParentDragIdleTick] = useState(0);
  const didAutoFitAfterTableGlbRef = useRef(false);
  const didAutoFitAfterShopifyMsgRef = useRef(false);
  /** True while Shopify parent is streaming custom mm (nacoDimStream ticks); cleared on commit messages. */
  const shopifyParentDimDragRef = useRef(false);
  /** Only bump shopifySyncTick once for initial embed camera fit — not on every parent slider postMessage */
  const didShopifyEmbedSyncBumpRef = useRef(false);

  // Materials
  const tableMatRef = useRef(
    new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0, roughness: 1 })
  );
  const legMatRef = useRef(
    new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0, roughness: 1 })
  );
  const chairMatRef = useRef(
    new THREE.MeshStandardMaterial({ color: 0x444444, metalness: 0.2, roughness: 0.7 })
  );

  const currentOutOffset = () =>
    chairDepth / 2 + CHAIR_OUT_OFFSET - (pushChairsIn ? clamp(tuckDepth, 0, MAX_TUCK) : 0);

  // ——— 桌子/桌腿：TintOnly 或 Wood 37（OakLight）。椅子保持 GLB 原材质，不随此项更新。———
  function applySurfaceMaterials() {
    // If LineTransparent temporarily replaced mesh materials, restore originals first.
    // Otherwise surface updates may be applied to the temporary override material and get "stuck".
    const restoreLineTransparentOverrides = (root: THREE.Object3D | null) => {
      if (!root) return;
      root.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!mesh.isMesh) return;
        if (mesh.userData.lineTransparentOriginalMaterial) {
          mesh.material = mesh.userData.lineTransparentOriginalMaterial;
          delete mesh.userData.lineTransparentOriginalMaterial;
        }
      });
    };
    restoreLineTransparentOverrides(tableGroupRef.current);
    restoreLineTransparentOverrides(chairsGroupRef.current);

    const mats = [tableMatRef.current, legMatRef.current].filter(Boolean) as THREE.MeshStandardMaterial[];
    mats.forEach((m) => {
      m.vertexColors = false;
      m.metalness = 0;
      m.transparent = false;
      m.opacity = 1;
      m.depthWrite = true;
      if (matMode === "OakLight") {
        applyWood37Surface(m);
      } else {
        stripStandardMaterialTextureMaps(m);
        m.roughnessMap = null;
        m.bumpMap = null;
        m.bumpScale = 0;
        m.roughness = 1; // Pure color mode uses uniform roughness
        m.color.set(
          matMode === "LineTransparent" ? 0xffffff : TIMBER_SPECS[timberKey].color
        );
      }
      m.needsUpdate = true;
    });

    const applyTimberToObjectRoot = (root: THREE.Object3D) => {
      root.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!mesh.isMesh || !mesh.material) return;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materials.forEach((mat) => {
          if (mat instanceof THREE.MeshStandardMaterial) {
            const m = mat;
            m.vertexColors = false;
            m.metalness = 0;
            m.transparent = false;
            m.opacity = 1;
            m.depthWrite = true;
            if (matMode === "OakLight") {
              applyWood37Surface(m);
            } else {
              stripStandardMaterialTextureMaps(m);
              m.roughnessMap = null;
              m.bumpMap = null;
              m.bumpScale = 0;
              m.roughness = 1;
              m.color.set(
                matMode === "LineTransparent" ? 0xffffff : TIMBER_SPECS[timberKey].color
              );
            }
            m.needsUpdate = true;
            return;
          }
          if (
            mat instanceof THREE.MeshBasicMaterial ||
            mat instanceof THREE.MeshLambertMaterial ||
            mat instanceof THREE.MeshPhongMaterial
          ) {
            mat.vertexColors = false;
            mat.map =
              matMode === "OakLight"
                ? mat.map ?? tableGlbEmbeddedWoodMap ?? wood37FallbackMap
                : null;
            if (matMode === "OakLight") {
              mat.color.set(0xffffff);
              mat.transparent = false;
              mat.opacity = 1;
              mat.depthWrite = true;
            } else {
              mat.color.set(
                matMode === "LineTransparent" ? 0xffffff : TIMBER_SPECS[timberKey].color
              );
              mat.transparent = false;
              mat.opacity = 1;
              mat.depthWrite = true;
            }
            mat.needsUpdate = true;
          }
        });
      });
    };

    const tableRoot = tableGroupRef.current;
    if (tableRoot) applyTimberToObjectRoot(tableRoot);
  }

  useEffect(() => { applySurfaceMaterials(); }, [matMode, timberKey]);

  useEffect(() => {
    let cancelled = false;
    const loader = new GLTFLoader();
    loader.load(
      TABLE_GLB_URL,
      (gltf) => {
        if (cancelled) return;
        tableGlbEmbeddedWoodMap = null;
        tableGlbUsesProceduralUV = false;
        scaleGltfRootToMmIfNeeded(gltf.scene);
        captureTableGlbEmbeddedWood(gltf.scene);
        // Capture world-mm anchors before centering geometry (centering zeros out centers).
        tableGlbBaseMetricsRef.current = captureTableGlbBaseMetrics(gltf.scene);
        centerTableGlbMeshGeometries(gltf.scene);
        tableGlbTemplateRef.current = gltf.scene;
        // Do not set width/depth/height from GLB: Shopify postMessage (or UI) owns target mm.
        setGlbReadyTick((t) => t + 1);
      },
      undefined,
      (err) => {
        console.warn("[Orimba] Failed to load table GLB", err);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  function clearLineArtEdges(root: THREE.Object3D) {
    root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const lineArtEdges = mesh.children.filter((c) => c.userData?.lineArtEdge === true);
      lineArtEdges.forEach((edgeObj) => {
        const edgeLine = edgeObj as THREE.LineSegments;
        mesh.remove(edgeLine);
        edgeLine.geometry.dispose();
        const edgeMat = edgeLine.material;
        if (Array.isArray(edgeMat)) edgeMat.forEach((m) => m.dispose());
        else edgeMat?.dispose?.();
      });
    });
  }

  function addLineArtEdges(root: THREE.Object3D) {
    root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh || !mesh.geometry) return;
      if (mesh.userData?.lineArtEdge === true) return;
      const edgesGeometry = new THREE.EdgesGeometry(mesh.geometry, 20);
      if (edgesGeometry.attributes.position.count === 0) {
        edgesGeometry.dispose();
        return;
      }
      const edgesMaterial = new THREE.LineBasicMaterial({
        color: 0x111111,
        transparent: true,
        opacity: 0.9,
      });
      const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
      edges.userData.lineArtEdge = true;
      mesh.add(edges);
    });
  }

  function applyRenderStyle() {
    const scene = sceneRef.current;
    if (!scene) return;

    const isLineArt = renderStyle === "LineArt";
    scene.background = new THREE.Color(isLineArt ? 0xffffff : 0xf3f4f6);

    if (hemiLightRef.current) hemiLightRef.current.intensity = isLineArt ? 0.85 : 1.0;
    if (dirLightRef.current) dirLightRef.current.intensity = isLineArt ? 0.45 : 0.8;
    if (ambientLightRef.current) ambientLightRef.current.intensity = isLineArt ? 0.5 : 0.2;

    const applyLineTransparentFill = (root: THREE.Object3D) => {
      root.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (!mesh.isMesh || mesh.userData?.lineArtEdge === true) return;
        const wantsLineTransparentSolid =
          matMode === "LineTransparent" && renderStyle === "Solid";
        const wantsLineTransparentLineArt =
          matMode === "LineTransparent" && renderStyle === "LineArt";
        if (wantsLineTransparentSolid || wantsLineTransparentLineArt) {
          if (!mesh.userData.lineTransparentOriginalMaterial) {
            mesh.userData.lineTransparentOriginalMaterial = mesh.material;
          }
          mesh.material = wantsLineTransparentSolid
            ? lineArtTransparentMatRef.current
            : lineArtFillMatRef.current;
          return;
        }
        if (mesh.userData.lineTransparentOriginalMaterial) {
          mesh.material = mesh.userData.lineTransparentOriginalMaterial;
          delete mesh.userData.lineTransparentOriginalMaterial;
        }
      });
    };

    const tableRoot = tableGroupRef.current;
    const chairsRoot = chairsGroupRef.current;
    if (tableRoot) {
      applyLineTransparentFill(tableRoot);
      clearLineArtEdges(tableRoot);
      if (isLineArt) addLineArtEdges(tableRoot);
    }
    if (chairsRoot) {
      applyLineTransparentFill(chairsRoot);
      clearLineArtEdges(chairsRoot);
      if (isLineArt) addLineArtEdges(chairsRoot);
    }
  }

  useEffect(() => {
    let cancelled = false;
    const loader = new GLTFLoader();
    loader.load(
      CHAIR_GLB_URL,
      (gltf) => {
        scaleGltfRootToMmIfNeeded(gltf.scene);
        chairGlbSceneSingleton = gltf.scene;
        if (cancelled) return;
        chairGlbTemplateRef.current = gltf.scene;
        setChairGlbReadyTick((t) => t + 1);
      },
      undefined,
      (err) => {
        console.warn("[Orimba] Failed to load chair GLB", err);
        chairGlbLoadFailed = true;
        setChairGlbReadyTick((t) => t + 1);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    if (chairGlbSceneSingleton) {
      chairGlbTemplateRef.current = chairGlbSceneSingleton;
    }
  }, []);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      const d = ev.data as Record<string, unknown>;
      if (!d || d.source !== "shopify-orimba") return;
      setEmbedShopify(true);
      const hasDimMsg =
        typeof d.widthMm === "number" ||
        typeof d.depthMm === "number" ||
        typeof d.heightMm === "number";
      if (hasDimMsg) {
        // Parent marks streaming slider ticks with nacoDimStream. Do not infer "drag end" from idle
        // timeouts — fast drags can sit at the final mm with no new messages while pointer is still down,
        // which previously fired a stale PDP price before pointerup flush.
        const dimStream = d.nacoDimStream === true;
        if (dimStream) {
          shopifyParentDimDragRef.current = true;
        } else {
          shopifyParentDimDragRef.current = false;
          setShopifyParentDragIdleTick((t) => t + 1);
        }
      }
      if (typeof d.widthMm === "number") setWidth(clamp(Math.round(d.widthMm as number), MIN_W, MAX_W));
      if (typeof d.depthMm === "number") setDepth(clamp(Math.round(d.depthMm as number), MIN_D, MAX_D));
      if (typeof d.heightMm === "number") setHeight(clamp(Math.round(d.heightMm as number), MIN_H, MAX_H));
      if (typeof d.timber === "string" && (d.timber as string).trim()) {
        setTimberKey(normalizeTimberLabel(d.timber as string));
      }
      if (!didShopifyEmbedSyncBumpRef.current) {
        didShopifyEmbedSyncBumpRef.current = true;
        setShopifySyncTick((t) => t + 1);
      }
    };
    window.addEventListener("message", onMsg);
    return () => {
      window.removeEventListener("message", onMsg);
    };
  }, []);

  // ---------------- Label & Dim Helpers ----------------
  function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + width, y, x + width, y + height, radius);
    ctx.arcTo(x + width, y + height, x, y + height, radius);
    ctx.arcTo(x, y + height, x, y, radius);
    ctx.arcTo(x, y, x + width, y, radius);
    ctx.closePath();
  }
  function makeLabelSprite(text: string) {
    const border = 8;
    const fontSize = 24;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    const display = `${text} mm`;
    ctx.font = `${fontSize}px Helvetica, Arial, sans-serif`;
    const textW = Math.ceil(ctx.measureText(display).width);
    const textH = fontSize * 1.6;
    const w = textW + border * 2;
    const h = textH + border * 2;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "rgba(0,0,0,0.8)";
    roundRect(ctx, 0, 0, w, h, 8);
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.font = `${fontSize}px Helvetica, Arial, sans-serif`;
    ctx.fillText(display, border, h / 2);
    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false }));
    sprite.scale.set(w, h, 1);
    return sprite;
  }
  function arrowWithTicks(start: THREE.Vector3, end: THREE.Vector3, labelText = "") {
    const g = new THREE.Group();
    g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([start, end]), new THREE.LineBasicMaterial({ color: 0x111111 })));
    const addTick = (at: THREE.Vector3) => {
      const dir = new THREE.Vector3().subVectors(end, start).normalize();
      const up = Math.abs(dir.y) < 0.99 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      const perp = new THREE.Vector3().crossVectors(dir, up).normalize();
      const half = 16;
      const a = at.clone().add(perp.clone().multiplyScalar(-half));
      const b = at.clone().add(perp.clone().multiplyScalar(half));
      g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([a, b]), new THREE.LineBasicMaterial({ color: 0x111111 })));
    };
    addTick(start);
    addTick(end);
    if (labelText) {
      const label = makeLabelSprite(labelText);
      const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
      label.position.copy(mid).add(new THREE.Vector3(0, 50, 0));
      g.add(label);
    }
    return g;
  }

  // --- Build table + dims ---
  const buildTableAndDims = (w: number, d: number, h: number, inset: number) => {
    if (!tableGroupRef.current || !dimsGroupRef.current) return;
    const tableGroup = tableGroupRef.current;
    tableGroup.clear();
    const g = dimsGroupRef.current!;
    g.clear();

    /** Skip Canvas-heavy dimension sprites while dragging (local range or Shopify parent sliders) */
    const skipDimAnnotations =
      isRangeDragging || (embedShopify && shopifyParentDimDragRef.current);

    const tableTmpl = tableGlbTemplateRef.current;
    const tableBase = tableGlbBaseMetricsRef.current;
    if (tableTmpl && tableBase) {
      const table = tableTmpl.clone(true);
      table.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.castShadow = true;
          mesh.receiveShadow = true;
        }
      });

      const pivotCount = applyParametricTableGlb(table, w, d, h, tableBase);
      if (pivotCount > 0) {
        table.updateMatrixWorld(true);
        const bb1 = new THREE.Box3().setFromObject(table);
        const cx = (bb1.min.x + bb1.max.x) / 2;
        const cz = (bb1.min.z + bb1.max.z) / 2;
        table.position.set(-cx, -bb1.min.y, -cz);
        tableGroup.add(table);

        table.updateMatrixWorld(true);
        const bb = new THREE.Box3().setFromObject(table);
        const gw = Math.max(1, bb.max.x - bb.min.x);
        const gd = Math.max(1, bb.max.z - bb.min.z);
        const gh = Math.max(1, bb.max.y - bb.min.y);
        const topY = bb.max.y;

        if (!skipDimAnnotations) {
          g.add(arrowWithTicks(new THREE.Vector3(-gw / 2, topY + 50, gd / 2 + 36), new THREE.Vector3(gw / 2, topY + 50, gd / 2 + 36), `${Math.round(w)}`));
          g.add(arrowWithTicks(new THREE.Vector3(gw / 2 + 36, topY + 50, -gd / 2), new THREE.Vector3(gw / 2 + 36, topY + 50, gd / 2), `${Math.round(d)}`));
          g.add(arrowWithTicks(new THREE.Vector3(-gw / 2 - 36, 0, gd / 2), new THREE.Vector3(-gw / 2 - 36, gh, gd / 2), `${Math.round(gh)}`));
        }
        return;
      }
      console.warn("[Orimba] Table GLB parametric assembly empty; using procedural table.");
    } else if (tableTmpl && !tableBase) {
      console.warn("[Orimba] Table GLB base metrics missing; using procedural table.");
    }

    // Top
    const top = new THREE.Mesh(new THREE.BoxGeometry(w, topThick, d), tableMatRef.current);
    top.position.y = h;
    tableGroup.add(top);

    const undersideY = h - topThick / 2;

    // Sled frames
    const frameDepth = Math.max(200, d - LEG_DEPTH_MARGIN * 2);
    const maxInset = Math.max(0, w / 2 - legBar / 2 - LEG_CLEAR_EDGE);
    const safeInset = clamp(inset, 0, maxInset);
    const rightX = w / 2 - safeInset - legBar / 2;
    const leftX = -rightX;

    // No gap to top
    const frameH = undersideY;

    const makeSledFrame = (xPos: number) => {
      const g = new THREE.Group();
      const postGeo = new THREE.BoxGeometry(legBar, frameH, LEG_THICKNESS_FIXED);
      const railGeo = new THREE.BoxGeometry(legBar, LEG_THICKNESS_FIXED, frameDepth);

      const postFront = new THREE.Mesh(postGeo, legMatRef.current);
      postFront.position.set(xPos, frameH / 2, -frameDepth / 2);
      const postBack = new THREE.Mesh(postGeo, legMatRef.current);
      postBack.position.set(xPos, frameH / 2, frameDepth / 2);
      const bottomRail = new THREE.Mesh(railGeo, legMatRef.current);
      bottomRail.position.set(xPos, LEG_THICKNESS_FIXED / 2, 0);

      g.add(postFront, postBack, bottomRail);
      return g;
    };

    tableGroup.add(makeSledFrame(rightX));
    tableGroup.add(makeSledFrame(leftX));

    // Aprons
    if (APRON_ON) {
      const innerFaceLeft = leftX + legBar / 2;
      const innerFaceRight = rightX - legBar / 2;
      const gapX = Math.max(0, innerFaceRight - innerFaceLeft);
      const gapZ = Math.max(0, frameDepth - LEG_THICKNESS_FIXED);
      const apronY = undersideY - apronH / 2;

      if (gapX > 0) {
        const apronLenX = Math.max(0, gapX - 2);
        if (apronLenX > 0) {
          const apronGeoX = new THREE.BoxGeometry(apronLenX, apronH, APRON_THICK);
          const centerX = (innerFaceLeft + innerFaceRight) / 2;
          const zFront = -frameDepth / 2 + LEG_THICKNESS_FIXED / 2 + APRON_THICK / 2;
          const zBack = frameDepth / 2 - LEG_THICKNESS_FIXED / 2 - APRON_THICK / 2;
          const frontApron = new THREE.Mesh(apronGeoX, legMatRef.current);
          frontApron.position.set(centerX, apronY, zFront);
          const backApron = new THREE.Mesh(apronGeoX, legMatRef.current);
          backApron.position.set(centerX, apronY, zBack);
          tableGroup.add(frontApron, backApron);
        }
      }
      if (gapZ > 0) {
        const apronLenZ = Math.max(0, gapZ - 2);
        if (apronLenZ > 0) {
          const apronGeoZ = new THREE.BoxGeometry(APRON_THICK, apronH, apronLenZ);
          const xLeftApron = leftX + legBar / 2 + APRON_THICK / 2;
          const xRightApron = rightX - legBar / 2 - APRON_THICK / 2;
          const leftApron = new THREE.Mesh(apronGeoZ, legMatRef.current);
          leftApron.position.set(xLeftApron, apronY, 0);
          const rightApron = new THREE.Mesh(apronGeoZ, legMatRef.current);
          rightApron.position.set(xRightApron, apronY, 0);
          tableGroup.add(leftApron, rightApron);
        }
      }
    }

    // Dims
    const topY = h + topThick / 2;
    if (!skipDimAnnotations) {
      g.add(arrowWithTicks(new THREE.Vector3(-w / 2, topY + 50, d / 2 + 36), new THREE.Vector3(w / 2, topY + 50, d / 2 + 36), `${Math.round(w)}`));
      g.add(arrowWithTicks(new THREE.Vector3(w / 2 + 36, topY + 50, -d / 2), new THREE.Vector3(w / 2 + 36, topY + 50, d / 2), `${Math.round(d)}`));
      g.add(arrowWithTicks(new THREE.Vector3(-w / 2 - 36, 0, d / 2), new THREE.Vector3(-w / 2 - 36, h, d / 2), `${Math.round(h)}`));

      if (showLegGapDim) {
        const rightX2 = w / 2 - clamp(legInset, 0, Math.max(0, w / 2 - legBar / 2 - LEG_CLEAR_EDGE)) - legBar / 2;
        const leftX2 = -rightX2;
        const innerFaceLeft2 = leftX2 + legBar / 2;
        const innerFaceRight2 = rightX2 - legBar / 2;
        const gap = Math.max(0, innerFaceRight2 - innerFaceLeft2);
        g.add(arrowWithTicks(new THREE.Vector3(innerFaceLeft2, topY + 20, d / 2 + 72), new THREE.Vector3(innerFaceRight2, topY + 20, d / 2 + 72), `${Math.round(gap)}`));
      }
    }

    if ((g as any).traverse) {
      (g as any).traverse((child: any) => {
        if (child.isSprite) {
          child.material.depthTest = false;
          child.renderOrder = 999;
        }
      });
    }
  };

  function makeChairMesh() {
    const tmpl = chairGlbTemplateRef.current ?? chairGlbSceneSingleton;
    if (tmpl) {
      if (!chairGlbTemplateRef.current) chairGlbTemplateRef.current = tmpl;
      return prepareChairGlbClone(tmpl, chairWidth, chairDepth, chairBackHeight);
    }
    if (!chairGlbLoadFailed) {
      return new THREE.Group();
    }

    const chair = new THREE.Group();
    const seatThick = 30;
    const seat = new THREE.Mesh(new THREE.BoxGeometry(chairWidth, seatThick, chairDepth), chairMatRef.current);
    seat.position.y = 420;
    chair.add(seat);

    const back = new THREE.Mesh(new THREE.BoxGeometry(chairWidth, Math.max(20, chairBackHeight - 420), 20), chairMatRef.current);
    back.position.set(0, 420 + Math.max(10, (chairBackHeight - 420) / 2), -chairDepth / 2 + 10);
    chair.add(back);

    const legThick = 30, legH = 420;
    [
      [chairWidth / 2 - legThick / 2, legH / 2, chairDepth / 2 - legThick / 2],
      [-chairWidth / 2 + legThick / 2, legH / 2, chairDepth / 2 - legThick / 2],
      [chairWidth / 2 - legThick / 2, legH / 2, -chairDepth / 2 + legThick / 2],
      [-chairWidth / 2 + legThick / 2, legH / 2, -chairDepth / 2 + legThick / 2],
    ].forEach(([x, y, z]) => {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(legThick, legH, legThick), chairMatRef.current);
      leg.position.set(x as number, y as number, z as number);
      chair.add(leg);
    });
    return chair;
  }

  function buildChairs(w: number, d: number, h: number, inset: number) {
    if (!chairsGroupRef.current) return;
    if (chairGlbSceneSingleton) {
      chairGlbTemplateRef.current = chairGlbSceneSingleton;
    }
    const group = chairsGroupRef.current;
    group.clear();
    let total = 0;

    const out = currentOutOffset();

    const maxInset = Math.max(0, w / 2 - legBar / 2 - LEG_CLEAR_EDGE);
    const safeInset = clamp(inset, 0, maxInset);
    const rightX = w / 2 - safeInset - legBar / 2;
    const leftX = -rightX;
    const innerFaceLeft = leftX + legBar / 2;
    const innerFaceRight = rightX - legBar / 2;
    const clearSpan = Math.max(0, innerFaceRight - innerFaceLeft);

    const CHAIR_TUCK_SIDE_MARGIN = 8;

    if (includeSideChairs && clearSpan > 0) {
      const gapCenterX = (innerFaceLeft + innerFaceRight) / 2;
      const xs = centeredPositions(clearSpan, chairWidth, chairClearance, CHAIR_CORNER_MARGIN).map(
        (xLocal) => gapCenterX + (xLocal - clearSpan / 2)
      );

      const zFront = d / 2 + out;
      const zBack = -d / 2 - out;

      xs.forEach((x) => {
        const half = chairWidth / 2;
        if (x - half < innerFaceLeft + CHAIR_TUCK_SIDE_MARGIN) return;
        if (x + half > innerFaceRight - CHAIR_TUCK_SIDE_MARGIN) return;

        const c1 = makeChairMesh();
        c1.position.set(x, 0, zFront);
        c1.rotation.y = Math.PI;
        group.add(c1);
        total++;

        const c2 = makeChairMesh();
        c2.position.set(x, 0, zBack);
        c2.rotation.y = 0;
        group.add(c2);
        total++;
      });
    }

    if (includeEndChairs) {
      const zs = centeredPositions(d, chairWidth, chairClearance, CHAIR_CORNER_MARGIN);
      const xRight = w / 2 + out;
      const xLeft = -w / 2 - out;
      zs.forEach((zLocal) => {
        const z = zLocal - d / 2;
        const c1 = makeChairMesh();
        c1.position.set(xRight, 0, z);
        c1.rotation.y = -Math.PI / 2;
        group.add(c1);
        total++;
        const c2 = makeChairMesh();
        c2.position.set(xLeft, 0, z);
        c2.rotation.y = Math.PI / 2;
        group.add(c2);
        total++;
      });
    }

    setChairCount(total);
    group.visible = showChairModels;
  }

  const frameCamera = (w: number, d: number, h: number) => {
    if (!cameraRef.current || !controlsRef.current) return;
    const out = showChairModels ? Math.max(0, currentOutOffset()) : 0;
    const maxW = w + 2 * out;
    const maxD = d + 2 * out;
    const maxH = showChairModels ? Math.max(h, chairBackHeight + 50) : h;
    const center = new THREE.Vector3(0, h / 2, 0);
    const fitOffset = 1.7;
    const maxDim = Math.max(maxW, maxD, maxH);
    const fov = (cameraRef.current as THREE.PerspectiveCamera).fov * (Math.PI / 180);
    const camZ = Math.abs(maxDim / (2 * Math.tan(fov / 2))) * fitOffset;
    (cameraRef.current as THREE.PerspectiveCamera).position.set(center.x, center.y + maxDim * 0.6, camZ);
    (cameraRef.current as THREE.PerspectiveCamera).lookAt(center);
    if (controlsRef.current.target) controlsRef.current.target.copy(center);
    controlsRef.current.update && controlsRef.current.update();
  };

  // Init：创建 3D、修复首帧 0 高问题、监听尺寸
  useEffect(() => {
    if (!mountRef.current) return;

    // WebGL 检查
    try {
      const testCanvas = document.createElement("canvas");
      const gl = testCanvas.getContext("webgl") || testCanvas.getContext("experimental-webgl");
      if (!gl) throw new Error("WebGL not supported");
    } catch {
      setInitError("WebGL not supported in this environment.");
      return;
    }

    // 场景
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f4f6);
    sceneRef.current = scene;

    // 相机
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 20000);
    camera.position.set(0, 800, 1600);
    cameraRef.current = camera;

    // 渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    renderer.domElement.style.touchAction = "none";
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // 灯光
    const hemi = new THREE.HemisphereLight(0xffffff, 0x888888, 1.0); // Balanced intensity
    hemi.position.set(0, 400, 0);
    scene.add(hemi);
    hemiLightRef.current = hemi;
    const dir = new THREE.DirectionalLight(0xffffff, 0.8); // Reduced intensity to avoid oily look
    dir.position.set(600, 900, 600);
    scene.add(dir);
    dirLightRef.current = dir;
    // Add subtle ambient light for better wood texture visibility
    const ambient = new THREE.AmbientLight(0xffffff, 0.2);
    scene.add(ambient);
    ambientLightRef.current = ambient;

    // 组
    const tableGroup = new THREE.Group(); scene.add(tableGroup); tableGroupRef.current = tableGroup;
    const dimsGroup = new THREE.Group(); scene.add(dimsGroup); dimsGroupRef.current = dimsGroup;
    const chairsGroup = new THREE.Group(); scene.add(chairsGroup); chairsGroupRef.current = chairsGroup;

    // 初始构建
    const h0 = Math.min(MAX_H, Math.max(MIN_H, height));
    buildTableAndDims(width, depth, h0, legInset);
    buildChairs(width, depth, h0, legInset);
    applySurfaceMaterials();
    applyRenderStyle();

    // 安全设置尺寸（延后一帧 + 监听）
    const setSize = () => {
      if (!mountRef.current || !rendererRef.current || !cameraRef.current) return;
      const rect = mountRef.current.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width || mountRef.current.clientWidth || window.innerWidth * 0.66));
      const h = Math.max(1, Math.floor(rect.height || mountRef.current.clientHeight || window.innerHeight));
      rendererRef.current.setSize(w, h, false);
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
    };
    requestAnimationFrame(setSize);
    const ro = new ResizeObserver(setSize);
    ro.observe(mountRef.current);

    // 动画
    let animRAF = 0;
    const animate = () => {
      animRAF = requestAnimationFrame(animate);
      if (controlsRef.current) controlsRef.current.update();
      renderer.render(scene, camera);
      if (!isReady) setIsReady(true);
    };
    animate();

    // 控制器
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.zoomSpeed = 0.9;
    controls.rotateSpeed = 0.9;
    controls.panSpeed = 0.8;
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.enableRotate = true;
    controls.minDistance = 80;
    controls.maxDistance = 8000;
    controls.target.set(0, height / 2, 0);
    controls.update();
    controlsRef.current = controls;

    // frameCamera must run after OrbitControls exists (otherwise it no-ops and the camera stays at defaults).
    frameCamera(width, depth, h0);

    // 清理
    return () => {
      cancelAnimationFrame(animRAF);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      scene.clear();
      if (renderer.domElement && renderer.domElement.parentElement) {
        renderer.domElement.parentElement.removeChild(renderer.domElement);
      }
      sceneRef.current = null;
      cameraRef.current = null;
      tableGroupRef.current = null;
      dimsGroupRef.current = null;
      chairsGroupRef.current = null;
      rendererRef.current = null;
      controlsRef.current = null;
      hemiLightRef.current = null;
      dirLightRef.current = null;
      ambientLightRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watchers（保持输入合法）
  useEffect(() => {
    const h = Math.min(MAX_H, Math.max(MIN_H, height));
    if (h !== height) setHeight(h);
  }, [height]);
  useEffect(() => {
    const t = Math.min(MAX_TUCK, Math.max(0, tuckDepth));
    if (t !== tuckDepth) setTuckDepth(t);
  }, [tuckDepth]);

  // Keep inset valid when width/legBar change
  useEffect(() => {
    const maxInset = Math.max(0, width / 2 - legBar / 2 - LEG_CLEAR_EDGE);
    const safeInset = Math.min(maxInset, Math.max(0, legInset));
    if (safeInset !== legInset) setLegInset(safeInset);
  }, [width, legInset, legBar]);

  // Rebuild 3D
  useEffect(() => {
    const h = Math.min(MAX_H, Math.max(MIN_H, height));
    const liteDrag =
      isRangeDragging || (embedShopify && shopifyParentDimDragRef.current);
    if (liteDrag) {
      // Lightweight update during slider drag (local or Shopify parent postMessage) to keep UI responsive.
      buildTableAndDims(width, depth, h, legInset);
      // Do not call frameCamera here — it resets zoom/pan the user set with OrbitControls.
      return;
    }
    buildTableAndDims(width, depth, h, legInset);
    buildChairs(width, depth, h, legInset);
    // Do not call frameCamera here — use "Reframe view" or initial mount only.
    applySurfaceMaterials();
    applyRenderStyle();
  }, [
    glbReadyTick,
    chairGlbReadyTick,
    width, depth, height, legInset, topThick, legBar, apronH,
    chairWidth, chairDepth, chairBackHeight, chairClearance,
    includeEndChairs, includeSideChairs, showChairModels, pushChairsIn, tuckDepth, showLegGapDim,
    isRangeDragging,
    embedShopify,
    shopifyParentDragIdleTick,
  ]);

  // One-shot fit after table GLB is ready (rebuild has run with real mesh bounds).
  useEffect(() => {
    if (glbReadyTick === 0) return;
    if (didAutoFitAfterTableGlbRef.current) return;
    if (!cameraRef.current || !controlsRef.current) return;
    const h = Math.min(MAX_H, Math.max(MIN_H, height));
    frameCamera(width, depth, h);
    didAutoFitAfterTableGlbRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot fit; omit frameCamera to avoid redundant deps
  }, [glbReadyTick, width, depth, height, showChairModels, chairBackHeight]);

  // One-shot fit on first Shopify embed sync (variant dimensions applied).
  useEffect(() => {
    if (shopifySyncTick === 0) return;
    if (didAutoFitAfterShopifyMsgRef.current) return;
    if (!cameraRef.current || !controlsRef.current) return;
    const h = Math.min(MAX_H, Math.max(MIN_H, height));
    frameCamera(width, depth, h);
    didAutoFitAfterShopifyMsgRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopifySyncTick, width, depth, height, showChairModels, chairBackHeight]);

  useEffect(() => {
    applyRenderStyle();
  }, [renderStyle, showChairModels, matMode]);

  const maxLegInset = Math.max(0, width / 2 - legBar / 2 - LEG_CLEAR_EDGE);

  // --- Geometry figures reused by pricing ---
  const geoFigures = useMemo(() => {
    const w = width, d = depth, h = Math.min(MAX_H, Math.max(MIN_H, height));
    const frameDepth = Math.max(200, d - LEG_DEPTH_MARGIN * 2);
    const maxInset = Math.max(0, w / 2 - legBar / 2 - LEG_CLEAR_EDGE);
    const safeInset = clamp(legInset, 0, maxInset);
    const rightX = w / 2 - safeInset - legBar / 2;
    const leftX = -rightX;
    const innerFaceLeft = leftX + legBar / 2;
    const innerFaceRight = rightX - legBar / 2;
    const gapX = Math.max(0, innerFaceRight - innerFaceLeft);
    const gapZ = Math.max(0, frameDepth - LEG_THICKNESS_FIXED);
    const undersideY = h - topThick / 2;
    const frameH = undersideY;

    return {
      topArea_mm2: w * d,
      topThick_mm: topThick,
      frameDepth_mm: frameDepth,
      frameH_mm: frameH,
      gapX_mm: gapX,
      gapZ_mm: gapZ,
      legBar_mm: legBar,
      legThickZ_mm: LEG_THICKNESS_FIXED,
    };
  }, [width, depth, height, topThick, legBar, legInset]);

  // --- Pricing breakdown ---
  const pricing = useMemo(() => {
    const dynamicTotalIncGST = calibratedPriceIncGST(width, depth, height, timberKey as string);

    const topVol_mm3 = geoFigures.topArea_mm2 * geoFigures.topThick_mm;
    const postsVol_mm3 = 4 * geoFigures.legBar_mm * geoFigures.legThickZ_mm * geoFigures.frameH_mm;
    const bottomRailsVol_mm3 = 2 * geoFigures.legBar_mm * geoFigures.legThickZ_mm * geoFigures.frameDepth_mm;
    const apronX_len = Math.max(0, geoFigures.gapX_mm - 2);
    const apronZ_len = Math.max(0, geoFigures.gapZ_mm - 2);
    const apronsXVol_mm3 = apronX_len > 0 ? 2 * apronX_len * apronH * APRON_THICK : 0;
    const apronsZVol_mm3 = apronZ_len > 0 ? 2 * APRON_THICK * apronH * apronZ_len : 0;

    const totalVol_mm3 = topVol_mm3 + postsVol_mm3 + bottomRailsVol_mm3 + apronsXVol_mm3 + apronsZVol_mm3;
    const totalVol_m3 = mm3_to_m3(totalVol_mm3);

    let timberCost = totalVol_m3 * woodPrice;
    let finishArea_m2 = mm2_to_m2(geoFigures.topArea_mm2) * 1.05;
    let finishCost = finishArea_m2 * finishPrice;
    let hours = PRICING_DEFAULTS.EST_ASSEMBLY_HOURS_BASE + PRICING_DEFAULTS.EST_ASSEMBLY_HOURS_PER_M * ((width + depth) / 1000);
    let labourCost = hours * labourRate;
    let deliveryCost = includeDelivery ? deliveryFlat : 0;

    let cogs = timberCost + finishCost + labourCost + deliveryCost;
    let exGST_legacy = cogs * (1 + markupPercent / 100);
    let gst_legacy = exGST_legacy * (gstPercent / 100);
    let total_legacy = exGST_legacy + gst_legacy;

    const s = total_legacy > 0 ? dynamicTotalIncGST / total_legacy : 1;

    timberCost *= s;
    finishCost *= s;
    labourCost *= s;
    deliveryCost *= s;
    cogs *= s;
    exGST_legacy *= s;
    gst_legacy *= s;

    return {
      m3: totalVol_m3,
      finishArea_m2,
      timberCost,
      finishCost,
      labourCost,
      deliveryCost,
      cogs,
      exGST: exGST_legacy,
      gst: gst_legacy,
      total: dynamicTotalIncGST,
      hours,
    };
  }, [
    geoFigures,
    woodPrice, finishPrice, labourRate, deliveryFlat, markupPercent, gstPercent, includeDelivery,
    width, depth, height, apronH, timberKey,
  ]);

  /** Shopify PDP (parent window): keep displayed price in sync with calibrated total from this iframe */
  useEffect(() => {
    if (!embedShopify) return;
    if (typeof window === "undefined") return;
    // Avoid spamming parent window + heavy host-page DOM updates while PDP sliders stream dimensions
    if (shopifyParentDimDragRef.current) return;
    try {
      const cents = Math.round(pricing.total * 100);
      const payload = {
        source: "naco-orimba-pricing",
        totalIncGst: pricing.total,
        totalIncGstCents: cents,
      };
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(payload, "*");
      }
    } catch {
      /* ignore */
    }
  }, [embedShopify, pricing.total, shopifyParentDragIdleTick]);

  const currency = (v: number) => v.toLocaleString(undefined, { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
  const number = (v: number, d = 2) => Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

  const reframeNow = () => {
    const h = Math.min(MAX_H, Math.max(MIN_H, height));
    frameCamera(width, depth, h);
  };

  const handleRangeInteractionStart = (e: React.PointerEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement | null;
    if (t instanceof HTMLInputElement && t.type === "range") {
      setIsRangeDragging(true);
    }
  };

  const handleRangeInteractionEnd = () => {
    setIsRangeDragging(false);
  };

  return (
    <div
      onPointerDownCapture={handleRangeInteractionStart}
      onPointerUpCapture={handleRangeInteractionEnd}
      onPointerCancelCapture={handleRangeInteractionEnd}
      className={[
        "w-full overflow-hidden",
        "h-[100dvh] max-h-[100dvh]",
        embedShopify
          ? "grid grid-cols-1 grid-rows-1"
          : "grid grid-cols-1 grid-rows-[minmax(220px,42vh)_minmax(0,1fr)] lg:grid-rows-1 lg:grid-cols-[minmax(0,2fr)_minmax(340px,1fr)]",
      ].join(" ")}
    >
      {/* 左：3D 视图（absolute 填满，避免 sticky 争议） */}
      <div className="relative h-full min-h-0 bg-gray-100 touch-manipulation">
        <div className="absolute inset-0" ref={mountRef} />
        {/* Overlay: chairs + reframe */}
        <div
          className={[
            "pointer-events-none absolute z-10",
            embedShopify
              ? "bottom-2 left-2 right-2 max-w-full"
              : "bottom-4 left-4 right-4 flex flex-nowrap items-center justify-end gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
          ].join(" ")}
        >
          <div
            className={[
              "flex items-center gap-1.5",
              embedShopify
                ? "flex-nowrap overflow-x-auto overscroll-x-contain rounded-lg border border-gray-200/90 bg-white/95 p-1.5 shadow-sm [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                : "contents",
            ].join(" ")}
          >
            {/* Badge only when chairs visible — avoids redundant "6 chairs · off" next to unchecked Show chairs */}
            {(!embedShopify || showChairModels) && (
              <span className="inline-block shrink-0 rounded-md bg-gray-700 text-white text-xs md:text-sm px-3 py-1.5 shadow whitespace-nowrap">
                {chairCount} chairs
              </span>
            )}
            {embedShopify && (
              <label className="pointer-events-auto flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 cursor-pointer select-none whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showChairModels}
                  onChange={(e) => setShowChairModels(e.target.checked)}
                />
                <span>
                  Show chairs
                  {!showChairModels ? ` (${chairCount})` : ""}
                </span>
              </label>
            )}
            {embedShopify && (
              <label className="pointer-events-auto flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 cursor-pointer select-none whitespace-nowrap">
                <span>Line art</span>
                <input
                  type="checkbox"
                  checked={renderStyle === "LineArt"}
                  onChange={(e) => setRenderStyle(e.target.checked ? "LineArt" : "Solid")}
                />
              </label>
            )}
            <button
              type="button"
              onClick={reframeNow}
              className="pointer-events-auto shrink-0 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs hover:bg-gray-50 whitespace-nowrap sm:text-sm"
            >
              Reframe view
            </button>
          </div>
        </div>

        {/* Error / Loading overlays */}
        {initError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 text-center p-6 z-20">
            <div className="max-w-md text-sm text-gray-800">
              <div className="font-semibold mb-1">3D view failed to start</div>
              <div className="mb-2">{initError}</div>
              <ul className="list-disc text-left ml-5 space-y-1">
                <li>Try refreshing this page.</li>
                <li>Use a WebGL-capable browser (Chrome/Firefox/Safari).</li>
                <li>Disable battery saver / low-power GPU mode.</li>
              </ul>
            </div>
          </div>
        )}
        {!initError && !isReady && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm z-10">
            Initializing 3D…
          </div>
        )}
      </div>

      {!embedShopify && (
      <div className="min-h-0 h-full overflow-y-auto overscroll-contain p-4 bg-white border-t lg:border-t-0 lg:border-l touch-manipulation">
        <div className="grid grid-cols-1 gap-6">
          {/* Orimba Table */}
          <section className="rounded-2xl border p-4">
            <h2 className="text-base font-semibold mb-3">Orimba Table</h2>

            {/* Surface */}
            <div className="mb-4">
              <div className="text-sm font-medium mb-2">Surface</div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setMatMode("TintOnly")}
                  className={`rounded-md border px-3 py-2 text-sm hover:bg-gray-50 ${matMode === "TintOnly" ? "ring-2 ring-gray-800" : ""}`}
                >
                  Tint Only（纯色）
                </button>
                <button
                  type="button"
                  onClick={() => setMatMode("OakLight")}
                  className={`rounded-md border px-3 py-2 text-sm hover:bg-gray-50 ${matMode === "OakLight" ? "ring-2 ring-gray-800" : ""}`}
                >
                  Wood 37
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMatMode("LineTransparent");
                  }}
                  className={`rounded-md border px-3 py-2 text-sm hover:bg-gray-50 ${matMode === "LineTransparent" ? "ring-2 ring-gray-800" : ""}`}
                >
                  Line Transparent
                </button>
              </div>
              {matMode === "OakLight" && (
                <p className="mt-1 text-xs text-gray-500">
                  使用 GLTFLoader 从 Tilbe Web.glb 加载的 Wood 37 材质与贴图（保留模型 roughness 等）。
                  {tableGlbUsesProceduralUV
                    ? " 当前模型无 UV，已自动生成展 UV，效果可能与 Blender 不一致；建议在导出时带上 UV。"
                    : ""}
                </p>
              )}
              {matMode === "LineTransparent" && (
                <p className="mt-1 text-xs text-gray-500">
                  仅保留线条轮廓；模型面为透明。会自动启用 Line Art。
                </p>
              )}
            </div>

            <div className="mb-4">
              <div className="text-sm font-medium mb-2">Render style</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRenderStyle("Solid")}
                  className={`rounded-md border px-3 py-2 text-sm hover:bg-gray-50 ${renderStyle === "Solid" ? "ring-2 ring-gray-800" : ""}`}
                >
                  Solid
                </button>
                <button
                  type="button"
                  onClick={() => setRenderStyle("LineArt")}
                  className={`rounded-md border px-3 py-2 text-sm hover:bg-gray-50 ${renderStyle === "LineArt" ? "ring-2 ring-gray-800" : ""}`}
                >
                  Line Art
                </button>
              </div>
            </div>

            {/* Timber（仅 TintOnly 模式可见） */}
            {matMode === "TintOnly" && (
              <div className="mb-4">
                <div className="text-sm font-medium mb-2">Timber</div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(TIMBER_SPECS).map(([key, spec]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTimberKey(key as keyof typeof TIMBER_SPECS)}
                      className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-gray-50 ${
                        timberKey === (key as any) ? "ring-2 ring-gray-800" : ""
                      }`}
                      title={`Set timber to ${key}`}
                    >
                      <span>{key}</span>
                      <span
                        className="ml-2 inline-block h-4 w-4 rounded"
                        style={{ backgroundColor: `#${(spec.color >>> 0).toString(16).padStart(6, "0")}` }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Basics */}
            <div className="flex flex-wrap gap-6 justify-start items-end">
              <label className="flex flex-col items-start text-sm w-44">
                <span className="mb-1 font-medium">Width (mm)</span>
                <input type="range" min={MIN_W} max={MAX_W} value={width} onChange={(e) => setWidth(Number(e.target.value))} />
                <input type="number" min={MIN_W} max={MAX_W} value={width} onChange={(e) => setWidth(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
              </label>
              <label className="flex flex-col items-start text-sm w-44">
                <span className="mb-1 font-medium">Depth (mm)</span>
                <input type="range" min={MIN_D} max={MAX_D} value={depth} onChange={(e) => setDepth(Number(e.target.value))} />
                <input type="number" min={MIN_D} max={MAX_D} value={depth} onChange={(e) => setDepth(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
              </label>
              <label className="flex flex-col items-start text-sm w-44">
                <span className="mb-1 font-medium">Height (mm)</span>
                <input type="range" min={MIN_H} max={MAX_H} value={height} onChange={(e) => setHeight(Number(e.target.value))} />
                <input type="number" min={MIN_H} max={MAX_H} value={height} onChange={(e) => setHeight(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
              </label>

              {/* Advanced Table Settings */}
              <details className="w-full rounded-lg border p-3">
                <summary className="cursor-pointer select-none font-medium">Advanced Table Settings</summary>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  <label className="flex flex-col min-w-0 text-sm">
                    <span className="mb-1 font-medium">Top thickness (mm)</span>
                    <input type="range" min={18} max={60} step={1} value={topThick} onChange={(e) => setTopThick(Number(e.target.value))} />
                    <input type="number" min={18} max={60} value={topThick} onChange={(e) => setTopThick(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
                  </label>

                  <label className="flex flex-col min-w-0 text-sm">
                    <span className="mb-1 font-medium">Leg width (mm)</span>
                    <input type="range" min={40} max={120} step={1} value={legBar} onChange={(e) => setLegBar(Number(e.target.value))} />
                    <input type="number" min={40} max={120} value={legBar} onChange={(e) => setLegBar(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
                  </label>

                  <label className="flex flex-col min-w-0 text-sm">
                    <span className="mb-1 font-medium">Apron height (mm)</span>
                    <input type="range" min={40} max={150} step={1} value={apronH} onChange={(e) => setApronH(Number(e.target.value))} />
                    <input type="number" min={40} max={150} value={apronH} onChange={(e) => setApronH(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
                  </label>

                  <label className="flex flex-col min-w-0 text-sm">
                    <span className="mb-1 font-medium">Leg inset from each end (mm)</span>
                    <input type="range" min={0} max={maxLegInset} step={1} value={legInset} onChange={(e) => setLegInset(Number(e.target.value))} />
                    <input type="number" min={0} max={Math.round(maxLegInset)} value={Math.round(legInset)} onChange={(e) => setLegInset(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
                    <span className="text-xs text-gray-500 mt-1">max {Math.round(maxLegInset)} mm</span>
                  </label>

                  <label className="flex items-center gap-2 text-sm sm:col-span-2 xl:col-span-3 border-t pt-3 mt-1">
                    <input type="checkbox" checked={showLegGapDim} onChange={(e) => setShowLegGapDim(e.target.checked)} />
                    <span>Show leg gap dimension</span>
                  </label>
                </div>
              </details>
            </div>
          </section>

          {/* Chair Controls */}
          <section className="rounded-2xl border p-4">
            <h2 className="text-base font-semibold mb-3">Chairs</h2>
            <div className="flex flex-wrap gap-6 justify-start items-end">
              <div className="flex flex-col gap-2 w-full">
                <label className="flex items-center gap-2 text-sm font-medium border-b pb-2 mb-1">
                  <input type="checkbox" checked={showChairModels} onChange={(e) => setShowChairModels(e.target.checked)} />
                  Show chairs in 3D
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={includeSideChairs} onChange={(e) => setIncludeSideChairs(e.target.checked)} />
                  Include side chairs
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={includeEndChairs} onChange={(e) => setIncludeEndChairs(e.target.checked)} />
                  Include end chairs
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={pushChairsIn} onChange={(e) => setPushChairsIn(e.target.checked)} />
                  Push chairs under table
                </label>
                {pushChairsIn && (
                  <label className="flex items-center gap-2 text-sm pl-6">
                    <span className="w-28">Tuck (mm)</span>
                    <input type="range" min={0} max={MAX_TUCK} value={tuckDepth} onChange={(e) => setTuckDepth(Number(e.target.value))} className="flex-1" />
                    <input type="number" min={0} max={MAX_TUCK} value={tuckDepth} onChange={(e) => setTuckDepth(Number(e.target.value))} className="border px-2 py-1 rounded-md w-24" />
                  </label>
                )}
              </div>

              <details className="w-full rounded-lg border p-3">
                <summary className="cursor-pointer select-none font-medium">Advanced Chair Settings</summary>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-6 items-end">
                  <label className="flex flex-col items-start text-sm w-40">
                    <span className="mb-1 font-medium">Chair width (mm)</span>
                    <input type="range" min={380} max={600} value={chairWidth} onChange={(e) => setChairWidth(Number(e.target.value))} />
                    <input type="number" min={380} max={600} value={chairWidth} onChange={(e) => setChairWidth(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
                  </label>
                  <label className="flex flex-col items-start text-sm w-40">
                    <span className="mb-1 font-medium">Chair depth (mm)</span>
                    <input type="range" min={400} max={650} value={chairDepth} onChange={(e) => setChairDepth(Number(e.target.value))} />
                    <input type="number" min={400} max={650} value={chairDepth} onChange={(e) => setChairDepth(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
                  </label>
                  <label className="flex flex-col items-start text-sm w-40">
                    <span className="mb-1 font-medium">Back height (mm)</span>
                    <input type="range" min={780} max={1000} value={chairBackHeight} onChange={(e) => setChairBackHeight(Number(e.target.value))} />
                    <input type="number" min={780} max={1000} value={chairBackHeight} onChange={(e) => setChairBackHeight(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
                  </label>
                  <label className="flex flex-col items-start text sm w-40">
                    <span className="mb-1 font-medium">Clearance (mm)</span>
                    <input type="range" min={60} max={240} value={chairClearance} onChange={(e) => setChairClearance(Number(e.target.value))} />
                    <input type="number" min={60} max={240} value={chairClearance} onChange={(e) => setChairClearance(Number(e.target.value))} className="border px-2 py-1 mt-1 rounded-md w-full" />
                  </label>
                </div>
              </details>

              <div className="text-xs text-gray-600">
                Calculated: <strong>{chairCount}</strong> chairs
              </div>
            </div>
          </section>

          {/* Pricing Panel */}
          <section className="rounded-2xl border p-4">
            <h2 className="text-base font-semibold mb-3">Pricing (AUD)</h2>

            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm text-gray-600">Total (inc. GST)</span>
              <span className="text-2xl md:text-3xl font-bold">{currency(pricing.total)}</span>
            </div>
            <div className="text-xs text-gray-500 mb-3">
              ex GST {currency(pricing.exGST)} · GST {currency(pricing.gst)}
            </div>

            <details className="rounded-lg border p-3 mb-3">
              <summary className="cursor-pointer select-none font-medium">View breakdown</summary>
              <div className="mt-2 text-xs text-gray-500">
                Timber volume: {number(pricing.m3, 3)} m³ · Finish area: {number(pricing.finishArea_m2, 2)} m² · Labour hours: {number(pricing.hours, 2)} h
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between"><span>Timber</span><span className="font-medium">{currency(pricing.timberCost)}</span></div>
                <div className="flex justify-between"><span>Finish</span><span className="font-medium">{currency(pricing.finishCost)}</span></div>
                <div className="flex justify-between"><span>Labour</span><span className="font-medium">{currency(pricing.labourCost)}</span></div>
                <div className="flex justify-between"><span>Delivery {includeDelivery ? "(incl.)" : "(—)"}</span><span className="font-medium">{currency(pricing.deliveryCost)}</span></div>
                <div className="flex justify-between border-t pt-2 mt-1 col-span-2"><span>COGS subtotal</span><span className="font-semibold">{currency(pricing.cogs)}</span></div>
                <div className="flex justify-between col-span-2"><span>Price ex GST</span><span className="font-semibold">{currency(pricing.exGST)}</span></div>
              </div>
            </details>

            <details className="rounded-lg border p-3">
              <summary className="cursor-pointer select-none font-medium">Cost settings</summary>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                <label className="flex items-center justify-between gap-3">
                  <span>Wood $/m³</span>
                  <input className="border rounded px-2 py-1 w-28" type="number" value={woodPrice} onChange={(e) => setWoodPrice(Number(e.target.value) || 0)} />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>Finish $/m²</span>
                  <input className="border rounded px-2 py-1 w-28" type="number" value={finishPrice} onChange={(e) => setFinishPrice(Number(e.target.value) || 0)} />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>Labour $/hr</span>
                  <input className="border rounded px-2 py-1 w-28" type="number" value={labourRate} onChange={(e) => setLabourRate(Number(e.target.value) || 0)} />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>Delivery flat</span>
                  <input className="border rounded px-2 py-1 w-28" type="number" value={deliveryFlat} onChange={(e) => setDeliveryFlat(Number(e.target.value) || 0)} />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>Markup %</span>
                  <input className="border rounded px-2 py-1 w-28" type="number" value={markupPercent} onChange={(e) => setMarkupPercent(Number(e.target.value) || 0)} />
                </label>
                <label className="flex items-center justify-between gap-3">
                  <span>GST %</span>
                  <input className="border rounded px-2 py-1 w-28" type="number" value={gstPercent} onChange={(e) => setGstPercent(Number(e.target.value) || 0)} />
                </label>
                <label className="flex items-center gap-2 col-span-2">
                  <input type="checkbox" checked={includeDelivery} onChange={(e) => setIncludeDelivery(e.target.checked)} />
                  <span>Include delivery</span>
                </label>
              </div>
            </details>
          </section>
        </div>
      </div>
      )}
    </div>
  );
}
